/**
 * Swirl Highlights Component
 * AI-curated "moments" in the cluster's history with horizontal scroll carousel
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, TrendingUp, Zap } from 'lucide-react';

export default function SwirlHighlights({ highlights, swirlTitle }) {
  if (!highlights || highlights.length === 0) {
    return (
      <div className="bg-white/60 backdrop-blur-sm rounded-xl border border-neutral-200 p-6 text-center">
        <Sparkles className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
        <p className="text-sm text-neutral-500" style={{ fontFamily: 'Inter, sans-serif' }}>
          Highlights will appear as significant moments emerge in this Swirl.
        </p>
      </div>
    );
  }

  const formatWeekDate = (weekStart) => {
    try {
      return new Date(weekStart).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return weekStart;
    }
  };

  const getHighlightText = (highlight) => {
    const itemCount = highlight.new_items || 0;
    const similarity = highlight.avg_similarity || 0;
    const tags = highlight.dominant_tags || [];

    if (itemCount > 5) {
      const tagText = tags.length > 0 ? tags[0] : 'content';
      return `Your curiosity peaked during the week of ${formatWeekDate(highlight.week_start)} when you added ${itemCount} new ${tagText} items.`;
    } else if (similarity > 0.85) {
      return `High cohesion week: ${formatWeekDate(highlight.week_start)} showed strong thematic alignment (${Math.round(similarity * 100)}% similarity).`;
    }
    return `Notable activity: ${formatWeekDate(highlight.week_start)}`;
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-neutral-800 mb-4" style={{ 
        fontFamily: 'Inter, sans-serif',
        fontSize: '16px',
        opacity: 0.9
      }}>
        Key Moments
      </h3>
      
      <div className="overflow-x-auto pb-4 scrollbar-hide">
        <div className="flex gap-4">
          {highlights.map((highlight, index) => (
            <motion.div
              key={highlight.id || index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
              className="flex-shrink-0 w-80 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border border-purple-100 p-5"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                  {highlight.new_items > 5 ? (
                    <TrendingUp className="w-5 h-5 text-white" />
                  ) : (
                    <Zap className="w-5 h-5 text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-neutral-700 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {getHighlightText(highlight)}
                  </p>
                  {highlight.dominant_tags && highlight.dominant_tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {highlight.dominant_tags.slice(0, 3).map((tag, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 bg-white/60 backdrop-blur-sm text-purple-700 text-xs rounded"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

